import BlockRange from '../block-range';
import Bold from './bold';
import Italic from './italic';
import Anchor from './anchor';
import Code from './code';
import Mention from './mention';
import Attribute from './attribute';
import Marker from './marker';
import parseEntities from '@intercom/embercom-composer/lib/entity-parser';
import ValidatedArraySet from '@intercom/embercom-composer/blocks/mixins/validated-array-set';
import { mapBy } from '@ember/object/computed';
import Em from 'ember';
export default Em.ArrayProxy.extend(ValidatedArraySet, {
  allowedTypes: [],
  validate(entity) {
    return !Em.isNone(entity);
  },
  setup: Em.on('init', function () {
    if (Em.isEmpty(this.get('content'))) {
      this.set('content', Em.A());
    }
  }),
  info: mapBy('content', 'info'),
  pushType(type, options) {
    if (this.get('allowedTypes').includes(type)) {
      let entity = this._createEntity(type, options);
      let entitiesToAdd = this._splitForSegregated(entity);
      this.pushObjects(entitiesToAdd);
    }
  },
  updateForDelete(block, deletedBlockRange) {
    this.withinRange(deletedBlockRange).forEach(function (entity) {
      entity.updateForDelete(block, deletedBlockRange);
    }, this);
    this.afterRange(deletedBlockRange).forEach(function (entity) {
      return entity.shiftLeftBy(deletedBlockRange.get('length'));
    });
    this.removeEmptyEntities();
    this.mergeAll();
  },
  toggle(type, blockRange, options) {
    let action;
    let existingEntities = this.byTypeWithinRange(type, blockRange);
    let segregatedWithinRange = this.segregatedWithinRange(blockRange);
    let entity = existingEntities.get('firstObject');
    if (existingEntities.get('length') === 0) {
      action = 'add';
    } else if (existingEntities.get('length') > 1 || segregatedWithinRange.get('length') > 0) {
      if (segregatedWithinRange) {
        if (this.isContiguousRange(existingEntities.concat(segregatedWithinRange))) {
          action = 'remove';
        } else {
          action = 'add';
        }
      } else {
        action = 'add';
      }
    } else if (entity.coversEdges(blockRange)) {
      action = 'remove';
    } else if (entity.intersectsRight(blockRange)) {
      action = 'remove';
    } else if (entity.intersectsLeft(blockRange)) {
      action = 'add';
    } else if (entity.isExactRange(blockRange)) {
      action = 'remove';
    } else if (entity.containedIn(blockRange)) {
      action = 'add';
    } else {
      action = 'add';
    }
    this[action](type, blockRange, options);
    return action === 'add';
  },
  add(type, blockRange, options) {
    options = options || {};
    options.startIndex = blockRange.get('startPoint.positionIndex');
    options.endIndex = blockRange.get('endPoint.positionIndex');
    this.pushType(type, options);
    this.mergeByType(type);
  },
  remove(type, blockRange) {
    let startIndex = blockRange.get('startPoint.positionIndex');
    let endIndex = blockRange.get('endPoint.positionIndex');
    let existingEntities = this.byTypeWithinRange(type, blockRange);
    existingEntities.forEach(function (entity) {
      if (entity.covers(blockRange)) {
        let copy = entity.copy();
        entity.contractFromRight(startIndex);
        copy.set('startIndex', endIndex);
        this.pushObject(copy);
        return;
      }
      if (entity.isExactRange(blockRange)) {
        entity.empty();
        return;
      }
      if (entity.containedIn(blockRange)) {
        entity.empty();
        return;
      }
      if (entity.intersectsLeft(blockRange)) {
        entity.contractFromRight(startIndex);
        return;
      }
      if (entity.intersectsRight(blockRange)) {
        entity.contractFromLeft(endIndex);
        return;
      }
    }.bind(this));
    this.removeEmptyEntities();
  },
  hasMarkers: Em.computed('content.[]', function () {
    return !!this.filterBy('type', 'marker').length;
  }),
  openersByIndex: Em.computed('content', 'content.[]', 'content.@each.startIndex', function () {
    return this.entitiesBy('startIndex');
  }),
  closersByIndex: Em.computed('content', 'content.[]', 'content.@each.endIndex', function () {
    return this.entitiesBy('endIndex');
  }),
  entitiesBy(startOrEnd) {
    let byIndex = [];
    if (!this.get('content')) {
      return [];
    }
    this.get('content').forEach(function (entity) {
      let startOrEndIndex = entity.get(startOrEnd);
      let precedence = entity.get('precedence');
      if (!byIndex[startOrEndIndex]) {
        byIndex[startOrEndIndex] = [];
      }
      byIndex[startOrEndIndex][precedence] = entity;
    });
    return byIndex;
  },
  shiftRightBy(delta) {
    this.get('content').forEach(function (entity) {
      entity.shiftRightBy(delta);
    });
  },
  getOpeners(index) {
    return this.filterBy('startIndex', index);
  },
  getClosers(index) {
    return this.filterBy('endIndex', index);
  },
  atIndex(index) {
    return this.filter(function (entity) {
      return entity && entity.isWithinRange(BlockRange.atPositionIndex(index));
    });
  },
  withinRange(blockRange) {
    return this.filter(function (entity) {
      return entity && entity.isWithinRange(blockRange);
    });
  },
  segregatedWithinRange(blockRange) {
    return this.filter(function (entity) {
      return entity && entity.get('segregated') && entity.isWithinRange(blockRange);
    });
  },
  afterRange(blockRange) {
    return this.filter(function (entity) {
      return entity && entity.isAfterRange(blockRange);
    });
  },
  beforeRange(blockRange) {
    return this.filter(function (entity) {
      return entity && entity.isBeforeRange(blockRange);
    });
  },
  byTypeWithinRange(type, blockRange) {
    return this.filter(function (entity) {
      return entity && entity.isType(type) && entity.isWithinRange(blockRange);
    });
  },
  byTypeCoveringRange(type, blockRange) {
    return this.filter(function (entity) {
      return entity && entity.isType(type) && entity.coversEdges(blockRange);
    });
  },
  byType(type) {
    return this.filter(function (entity) {
      return entity && entity.isType(type);
    });
  },
  removeEmptyEntities() {
    this.set('content', this.get('content').filter(function (entity) {
      return entity.get('allowEmpty') || entity && entity.get('hasLength');
    }));
  },
  mergeAll() {
    this.filterBy('isMergeable').mapBy('type').uniq().forEach(function (type) {
      this.mergeByType(type);
    }, this);
  },
  mergeByType(type) {
    if (type === 'attribute') {
      return;
    }
    let entitiesByType = this.byType(type);
    if (entitiesByType.get('length') <= 1) {
      return;
    }
    let openersByIndex = [];
    let closersByIndex = [];
    let lowestStartIndex = Number.POSITIVE_INFINITY;
    let highestEndIndex = 0;
    entitiesByType.forEach(function (entity) {
      let startIndex = entity.get('startIndex');
      if (startIndex < lowestStartIndex) {
        lowestStartIndex = startIndex;
      }
      let endIndex = entity.get('endIndex');
      if (endIndex > highestEndIndex) {
        highestEndIndex = endIndex;
      }
      if (!openersByIndex[startIndex]) {
        openersByIndex[startIndex] = [];
      }
      if (!closersByIndex[endIndex]) {
        closersByIndex[endIndex] = [];
      }
      openersByIndex[startIndex].push(entity);
      closersByIndex[endIndex].push(entity);
    });
    let activeEntity;
    let openEntities = 0;
    let entitiesToRemove = [];
    for (let i = lowestStartIndex; i <= highestEndIndex; i++) {
      if (openersByIndex[i]) {
        openEntities++;
        if (!activeEntity) {
          activeEntity = openersByIndex[i].pop();
        }
        entitiesToRemove = entitiesToRemove.concat(openersByIndex[i]);
      }
      if (closersByIndex[i] && activeEntity) {
        openEntities--;
        if (openEntities === 0) {
          activeEntity.expandToRight(i);
          activeEntity = null;
        }
      }
    }
    this.removeObjects(entitiesToRemove);
  },
  _createEntity(type, options) {
    return this._getEntityClass(type).create(options);
  },
  _getEntityClass(type) {
    switch (type) {
      case 'bold':
        return Bold;
      case 'italic':
        return Italic;
      case 'anchor':
        return Anchor;
      case 'mention':
        return Mention;
      case 'attribute':
        return Attribute;
      case 'marker':
        return Marker;
      case 'code':
        return Code;
      default:
        throw new Error(`Invalid entity type ${type}`);
    }
  },
  _getEntityNameForNode(node) {
    switch (node.tagName) {
      case 'B':
        return 'bold';
      case 'A':
        return 'anchor';
      case 'I':
        return 'italic';
      default:
        throw new Error(`Unknown tagName ${node.tagName}`);
    }
  },
  _splitForSegregated(baseEntity) {
    let blockRange = BlockRange.atPositionIndex(baseEntity.get('startIndex') + 1, baseEntity.get('endIndex') - 1);
    let segregatedWithinRange = this.segregatedWithinRange(blockRange);
    if (segregatedWithinRange.length === 0) {
      return [baseEntity];
    }
    let entitiesToAdd = [];
    let sortedBoundries = this.getSortedBoundries(segregatedWithinRange);
    if (baseEntity.get('startIndex') < sortedBoundries.startIndexes[0]) {
      let entityCopy = baseEntity.copy();
      entityCopy.setProperties({
        endIndex: sortedBoundries.startIndexes[0]
      });
      entitiesToAdd.push(entityCopy);
    }
    if (baseEntity.get('endIndex') > sortedBoundries.endIndexes[segregatedWithinRange.length - 1]) {
      let entityCopy = baseEntity.copy();
      entityCopy.setProperties({
        startIndex: sortedBoundries.endIndexes[segregatedWithinRange.length - 1]
      });
      entitiesToAdd.push(entityCopy);
    }
    if (segregatedWithinRange.length > 1) {
      for (let i = 0; i < segregatedWithinRange.length - 1; i++) {
        let entityCopy = baseEntity.copy();
        entityCopy.setProperties({
          startIndex: sortedBoundries.endIndexes[i],
          endIndex: sortedBoundries.startIndexes[i + 1]
        });
        entitiesToAdd.push(entityCopy);
      }
    }
    return entitiesToAdd;
  },
  isContiguousRange(entities) {
    let sortedBoundries = this.getSortedBoundries(entities);
    for (let i = 0; i < entities.length - 1; i++) {
      if (sortedBoundries.endIndexes[i] < sortedBoundries.startIndexes[i + 1]) {
        return false;
      }
    }
    return true;
  },
  getSortedBoundries(entities) {
    let startIndexes = [];
    let endIndexes = [];
    entities.forEach(function (entity) {
      startIndexes.push(entity.get('startIndex'));
      endIndexes.push(entity.get('endIndex'));
    });
    startIndexes.sort(sortNumber);
    endIndexes.sort(sortNumber);
    return {
      startIndexes,
      endIndexes
    };
  }
}).reopenClass({
  parseEntities(text, allowedTypes, config) {
    let entityList = this.create({
      config,
      allowedTypes
    });
    let cleanedText = parseEntities(entityList, text, config);
    return Em.Object.create({
      cleanedText,
      entityList
    });
  },
  compare(entitiesA, entitiesB) {
    if (entitiesA.get('length') !== entitiesB.get('length')) {
      return false;
    }
    for (let i = 0; i < entitiesA.get('length'); i++) {
      if (!entitiesA.objectAt(i).isEqualTo(entitiesB.objectAt(i))) {
        return false;
      }
    }
    return true;
  }
});
function sortNumber(a, b) {
  return a - b;
}